// ===========================================
// Image Utilities for OCR Capture
// ===========================================

/**
 * Captures a frame from a video element as a canvas
 * @param video - HTMLVideoElement to capture from
 * @returns Canvas element with the captured frame, or null if capture fails
 */
export const captureVideoFrame = (video: HTMLVideoElement): HTMLCanvasElement | null => {
  if (!video || video.readyState < 2) {
    console.warn('[ImageUtils] Video not ready for capture');
    return null;
  }

  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    console.warn('[ImageUtils] Could not get canvas context');
    return null;
  }

  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  return canvas;
};

/**
 * Crops canvas to center region (focuses on text area)
 * @param canvas - Source canvas
 * @param cropRatio - Ratio of original size to keep (0-1, default 0.6)
 * @returns New canvas with cropped image
 */
export const cropToCenter = (
  canvas: HTMLCanvasElement,
  cropRatio: number = 0.6
): HTMLCanvasElement => {
  const croppedCanvas = document.createElement('canvas');

  const cropWidth = Math.floor(canvas.width * cropRatio);
  const cropHeight = Math.floor(canvas.height * cropRatio);
  const offsetX = Math.floor((canvas.width - cropWidth) / 2);
  const offsetY = Math.floor((canvas.height - cropHeight) / 2);

  croppedCanvas.width = cropWidth;
  croppedCanvas.height = cropHeight;

  const ctx = croppedCanvas.getContext('2d');
  if (!ctx) {
    return canvas; // Return original if crop fails
  }

  ctx.drawImage(
    canvas,
    offsetX,
    offsetY,
    cropWidth,
    cropHeight,
    0,
    0,
    cropWidth,
    cropHeight
  );

  return croppedCanvas;
};

/**
 * Compresses canvas to base64 JPEG with size constraints
 * @param canvas - Canvas to compress
 * @param quality - JPEG quality (0-1)
 * @param maxSizeKB - Maximum output size in KB
 * @returns Base64 encoded JPEG string (without data URL prefix)
 */
export const compressImageToBase64 = async (
  canvas: HTMLCanvasElement,
  quality: number = 0.8,
  maxSizeKB: number = 500
): Promise<string> => {
  const maxSizeBytes = maxSizeKB * 1024;
  let currentQuality = quality;
  let base64: string;

  // Try compression with decreasing quality until size limit is met
  do {
    const dataUrl = canvas.toDataURL('image/jpeg', currentQuality);
    base64 = dataUrl.replace(/^data:image\/jpeg;base64,/, '');

    // Estimate actual byte size (base64 is ~4/3 larger than binary)
    const estimatedBytes = Math.ceil((base64.length * 3) / 4);

    if (estimatedBytes <= maxSizeBytes) {
      break;
    }

    // Reduce quality and try again
    currentQuality -= 0.1;

    // If quality is too low, scale down the image instead
    if (currentQuality < 0.3) {
      const scaledCanvas = scaleCanvas(canvas, 0.75);
      return compressImageToBase64(scaledCanvas, 0.7, maxSizeKB);
    }
  } while (currentQuality > 0.1);

  return base64;
};

/**
 * Scales a canvas to a smaller size
 * @param canvas - Source canvas
 * @param scale - Scale factor (0-1)
 * @returns Scaled canvas
 */
export const scaleCanvas = (
  canvas: HTMLCanvasElement,
  scale: number
): HTMLCanvasElement => {
  const scaledCanvas = document.createElement('canvas');
  scaledCanvas.width = Math.floor(canvas.width * scale);
  scaledCanvas.height = Math.floor(canvas.height * scale);

  const ctx = scaledCanvas.getContext('2d');
  if (!ctx) {
    return canvas;
  }

  // Use better quality scaling
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(canvas, 0, 0, scaledCanvas.width, scaledCanvas.height);

  return scaledCanvas;
};

/**
 * Converts a File/Blob to base64 string
 * @param file - File or Blob to convert
 * @returns Base64 encoded string (without data URL prefix)
 */
export const fileToBase64 = (file: File | Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix
      const base64 = result.replace(/^data:[^;]+;base64,/, '');
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

/**
 * Creates a data URL from base64 string
 * @param base64 - Base64 encoded image data
 * @param mimeType - MIME type (default: image/jpeg)
 * @returns Data URL string
 */
export const base64ToDataUrl = (
  base64: string,
  mimeType: string = 'image/jpeg'
): string => {
  return `data:${mimeType};base64,${base64}`;
};

/**
 * Estimates the byte size of a base64 string
 * @param base64 - Base64 encoded string
 * @returns Estimated byte size
 */
export const estimateBase64Size = (base64: string): number => {
  // Remove padding characters
  const padding = (base64.match(/=/g) || []).length;
  // Base64 encodes 3 bytes in 4 characters
  return Math.ceil((base64.length * 3) / 4) - padding;
};

/**
 * Applies image preprocessing for better OCR results
 * - Converts to grayscale
 * - Increases contrast
 * - Applies sharpening convolution
 * @param canvas - Source canvas
 * @returns Processed canvas
 */
export const preprocessForOCR = (canvas: HTMLCanvasElement): HTMLCanvasElement => {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return canvas;
  }

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  // Convert to grayscale and increase contrast
  for (let i = 0; i < data.length; i += 4) {
    const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];

    const contrast = 1.3;
    const factor = (259 * (contrast * 255 + 255)) / (255 * (259 - contrast * 255));
    const enhanced = factor * (gray - 128) + 128;
    const clamped = Math.max(0, Math.min(255, enhanced));

    data[i] = clamped;
    data[i + 1] = clamped;
    data[i + 2] = clamped;
  }

  ctx.putImageData(imageData, 0, 0);

  // Apply sharpening convolution kernel
  applySharpen(ctx, canvas.width, canvas.height);

  return canvas;
};

/**
 * Applies a 3x3 sharpening convolution to enhance text edges
 */
const applySharpen = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
  const src = ctx.getImageData(0, 0, width, height);
  const dst = ctx.createImageData(width, height);
  const srcData = src.data;
  const dstData = dst.data;

  // Mild sharpen kernel: enhances edges without oversharpening
  // [0, -1, 0]
  // [-1, 5, -1]
  // [0, -1, 0]
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const idx = (y * width + x) * 4;
      for (let c = 0; c < 3; c++) {
        const val =
          -srcData[((y - 1) * width + x) * 4 + c]
          - srcData[(y * width + (x - 1)) * 4 + c]
          + 5 * srcData[idx + c]
          - srcData[(y * width + (x + 1)) * 4 + c]
          - srcData[((y + 1) * width + x) * 4 + c];
        dstData[idx + c] = Math.max(0, Math.min(255, val));
      }
      dstData[idx + 3] = 255; // Alpha
    }
  }

  ctx.putImageData(dst, 0, 0);
};

/**
 * Measures average brightness of a canvas (0-255).
 * Returns low values for dark images.
 */
export const measureBrightness = (canvas: HTMLCanvasElement): number => {
  const ctx = canvas.getContext('2d');
  if (!ctx) return 128;

  // Sample a center region for performance
  const sampleSize = Math.min(100, canvas.width, canvas.height);
  const sx = Math.floor((canvas.width - sampleSize) / 2);
  const sy = Math.floor((canvas.height - sampleSize) / 2);
  const imageData = ctx.getImageData(sx, sy, sampleSize, sampleSize);
  const data = imageData.data;

  let totalBrightness = 0;
  const pixelCount = data.length / 4;
  for (let i = 0; i < data.length; i += 4) {
    totalBrightness += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
  }

  return totalBrightness / pixelCount;
};
